import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { getFormattedFullAddress } from 'app/pages/loanApplication/LoanApplication.utils';
import { getHasInterestCreditValue } from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/ClosingInformation/ClosingInformation.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import {
  AttendantCompanyTypeEnum,
  HelocPurposeType,
  LoanType,
  OccupancyType,
  TRUST_ENTITY_NAMES_NUMBER,
} from 'constants/loanApplication';
import { statesWithNationalCategoryOptions } from 'constants/options';
import {
  getCleanPhoneNumber,
  getConditionalObject,
  normalizeRequestPhoneNumber,
  replaceEmptyWithNull,
} from 'utils/data';
import { getCommonDateBody, getCommonDateTimeBody } from 'utils/date';
import { checkIsFormChanged } from 'utils/form';
import {
  checkIsAdditionalLoanFlow,
  checkIsHelocLoanType,
  checkIsRefinanceFlow,
} from 'utils/loanApplication';
import { checkIsChattelRescindable, getDefaultDisbursementDate } from 'utils/loanPrepaidsDates';

import { getAttorneyFieldIds, getCompanyFieldIds } from '../EditDrawer.companyConfig';

import {
  ClosingFundingDatesFormFieldId,
  FundingInformationFieldId,
  PREPAID_INTEREST_CREDIT_FORM_FIELD_IDS,
  SchedulingFieldId,
  TitleHolderFieldId,
  TitleInstructionsFieldId,
  TRUST_ENTITY_TYPES,
  TrustFieldId,
  WireToInformationFieldId,
} from './Content.constants';

export const getClearFields = fieldId =>
  Object.fromEntries(Object.values(fieldId).map(key => [key, '']));

export const getTimestamp = value => (value ? dayjs(value).valueOf() : null);

// The stored note and funding dates seed the drawer's date state; the funding date bounds a chattel
// first payment (LEN-198).
export const getInitialInterestCreditTimestamps = closingInfo => ({
  noteDate: getTimestamp(closingInfo?.prepaidInterestCreditData?.interestCreditNoteDate),
  fundingDate: getTimestamp(closingInfo?.prepaidInterestCreditData?.interestCreditFundingDate),
});

// Maturity Date is server-computed as the due date of the final scheduled payment:
// firstPaymentDate + (term - 1) months. When the program gives no term, we recover it
// from the loan's persisted (firstPaymentDate, maturityDate) pair so the read-only
// field can still be re-projected live when the payment dates cascade.
export const getMaturityTermMonths = (firstPaymentDate, maturityDate) => {
  if (_.isNil(firstPaymentDate) || _.isNil(maturityDate)) {
    return null;
  }

  const monthsBetween = dayjs(maturityDate).diff(dayjs(firstPaymentDate), 'month', true);

  // Reject before rounding: a maturity a few days BEFORE the first payment rounds to -0 and
  // would otherwise pass as a one-month term, projecting maturity onto the first payment date.
  if (monthsBetween < 0) {
    return null;
  }

  return Math.round(monthsBetween) + 1;
};

export const getMaturityDateByFirstPaymentDate = (firstPaymentDate, maturityTermMonths) =>
  !_.isNil(firstPaymentDate) && maturityTermMonths > 0
    ? dayjs(firstPaymentDate).add(maturityTermMonths - 1, 'month')
    : null;

// Rescission applies only to a primary residence: a retail refinance, a non-purchase HELOC, or a
// chattel loan that is not an explicit purchase (LEN-198).
export const getIsRescissionApplicable = ({ loanApplication, subjectPropertyOccupancy }) => {
  const loanPurpose = loanApplication?.application?.loanPurpose;
  const helocPurpose = loanApplication?.application?.helocPurpose;
  const isPrimary = subjectPropertyOccupancy === OccupancyType.PRIMARY_RESIDENCE;

  return (
    isPrimary &&
    (checkIsRefinanceFlow(loanPurpose) ||
      (checkIsAdditionalLoanFlow(loanPurpose) && helocPurpose !== HelocPurposeType.HOME_PURCHASE) ||
      checkIsChattelRescindable(loanApplication))
  );
};

const getPositiveTerm = term => (term > 0 ? term : null);

// Mirrors the server's MaturityDateCalculator term selection so a loan with no
// persisted maturity still projects one live once its payment dates are set.
// Enum values match the backend ProgramLoanType.
export const getMaturityTermMonthsFromProgram = ({
  programLoanType,
  loanTerm,
  initialDrawPeriodMonths,
  repaymentPeriodMonths,
} = {}) => {
  if (programLoanType === LoanType.REVERSE_MORTGAGE) {
    return null;
  }

  if (checkIsHelocLoanType(programLoanType)) {
    return _.isNil(initialDrawPeriodMonths) || _.isNil(repaymentPeriodMonths)
      ? null
      : getPositiveTerm(initialDrawPeriodMonths + repaymentPeriodMonths);
  }

  return getPositiveTerm(loanTerm);
};

// Prefers the program-derived term (what the server will compute) so a loan with no persisted
// maturity still projects one. Only a closed-end loan falls back to its persisted dates: the server
// gives a HELOC without a program term or a reverse mortgage no maturity, so a stored one is stale.
export const getProjectedMaturityTermMonths = ({
  maturityCalcInputs,
  firstPaymentDate,
  maturityDate,
}) => {
  const { programLoanType } = maturityCalcInputs;
  const hasPersistedTermFallback =
    !checkIsHelocLoanType(programLoanType) && programLoanType !== LoanType.REVERSE_MORTGAGE;

  return (
    getMaturityTermMonthsFromProgram(maturityCalcInputs) ??
    (hasPersistedTermFallback ? getMaturityTermMonths(firstPaymentDate, maturityDate) : null)
  );
};

export const getDisabledConfig = config => {
  if (!config?.groups) {
    return config;
  }

  return {
    ...config,
    groups: config.groups.map(group => ({
      ...group,
      fields: group.fields?.map(field => ({
        ...field,
        inputProps: {
          ...field.inputProps,
          disabled: true,
        },
      })),
      ...(group.additionalGroupContentConfig && {
        additionalGroupContentConfig: {
          ...group.additionalGroupContentConfig,
          fields: group.additionalGroupContentConfig.fields?.map(field => ({
            ...field,
            disabled: true,
          })),
        },
      }),
    })),
  };
};

export const buildClosingPayload = (
  formData,
  {
    hasInterestCredit,
    loanApplication,
    subjectPropertyState,
    businessDays,
    includePrepaidInterestCreditData = true,
    includeLockGatedFundingData = true,
  } = {},
) =>
  replaceEmptyWithNull({
    ...(SchedulingFieldId.ANTICIPATED_CLOSING_DATE in formData
      ? {
          [SchedulingFieldId.ANTICIPATED_CLOSING_DATE]: getCommonDateBody(
            formData[SchedulingFieldId.ANTICIPATED_CLOSING_DATE],
          ),
        }
      : {}),
    ...(SchedulingFieldId.FIRST_CLOSE_AT in formData
      ? {
          [SchedulingFieldId.FIRST_CLOSE_AT]: getCommonDateTimeBody(
            formData[SchedulingFieldId.FIRST_CLOSE_AT],
          ),
        }
      : {}),
    ...(SchedulingFieldId.CLOSE_AT in formData
      ? {
          [SchedulingFieldId.CLOSE_AT]: getCommonDateTimeBody(formData[SchedulingFieldId.CLOSE_AT]),
        }
      : {}),
    ...(SchedulingFieldId.LOCATION in formData
      ? {
          [SchedulingFieldId.LOCATION]:
            formData[SchedulingFieldId.LOCATION]?.value ?? formData[SchedulingFieldId.LOCATION],
        }
      : {}),
    ...(includeLockGatedFundingData && ClosingFundingDatesFormFieldId.APPROVAL_DATE in formData
      ? {
          [ClosingFundingDatesFormFieldId.APPROVAL_DATE]: getCommonDateBody(
            formData[ClosingFundingDatesFormFieldId.APPROVAL_DATE],
          ),
        }
      : {}),
    ...(includeLockGatedFundingData &&
    ClosingFundingDatesFormFieldId.APPROVAL_EXPIRATION_DATE in formData
      ? {
          [ClosingFundingDatesFormFieldId.APPROVAL_EXPIRATION_DATE]: getCommonDateBody(
            formData[ClosingFundingDatesFormFieldId.APPROVAL_EXPIRATION_DATE],
          ),
        }
      : {}),
    ...(includeLockGatedFundingData && ClosingFundingDatesFormFieldId.MATURITY_DATE in formData
      ? {
          [ClosingFundingDatesFormFieldId.MATURITY_DATE]: getCommonDateBody(
            formData[ClosingFundingDatesFormFieldId.MATURITY_DATE],
          ),
          [ClosingFundingDatesFormFieldId.IS_MATURITY_DATE_SET_MANUALLY]: false,
        }
      : {}),
    ...getConditionalObject(TitleInstructionsFieldId.SPECIAL_ENDORSEMENT in formData, {
      [TitleInstructionsFieldId.SPECIAL_ENDORSEMENT]:
        formData[TitleInstructionsFieldId.SPECIAL_ENDORSEMENT],
    }),
    ...(includePrepaidInterestCreditData &&
    ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE in formData
      ? {
          prepaidInterestCreditData: replaceEmptyWithNull({
            [ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE]: getCommonDateBody(
              formData[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_NOTE_DATE],
            ),
            [ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE]: getCommonDateBody(
              formData[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_RESCISSION_DATE],
            ),
            [ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE]: getCommonDateBody(
              formData[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE],
            ),
            [ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE]: getCommonDateBody(
              formData[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_DISBURSEMENT_DATE],
            ),
            [ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE]: getCommonDateBody(
              formData[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FIRST_PAYMENT_DATE],
            ),
            hasInterestCredit: getHasInterestCreditValue({
              fundingDate: getDefaultDisbursementDate(
                formData[ClosingFundingDatesFormFieldId.INTEREST_CREDIT_FUNDING_DATE],
                businessDays,
              ),
              hasInterestCredit,
              loanApplication,
              subjectPropertyState,
            }),
          }),
        }
      : {}),
  });

export const checkIsPrepaidInterestCreditDataChanged = ({ currentFormData, initialFormData }) =>
  checkIsFormChanged({
    currentForm: _.pick(currentFormData, PREPAID_INTEREST_CREDIT_FORM_FIELD_IDS),
    initialForm: _.pick(initialFormData, PREPAID_INTEREST_CREDIT_FORM_FIELD_IDS),
  });

export const buildClosingPayloadOptions = ({
  hasInterestCredit,
  loanApplication,
  subjectPropertyState,
  businessDays,
  isLockGatedFieldsDisabled,
  allFormData,
  initialFormValues,
}) => ({
  hasInterestCredit,
  loanApplication,
  subjectPropertyState,
  businessDays,
  // Entering a closing date cascades derived funding-section values into the form. While
  // those sections are lock-gated the user cannot review them, so none of their fields —
  // interest credit, approval dates or maturity date — may be persisted.
  includeLockGatedFundingData: !isLockGatedFieldsDisabled,
  includePrepaidInterestCreditData:
    !isLockGatedFieldsDisabled &&
    checkIsPrepaidInterestCreditDataChanged({
      currentFormData: allFormData,
      initialFormData: initialFormValues,
    }),
});

const buildTrustEntityFields = (trustData, entityTypes, buildKeys) => {
  const fields = {};

  entityTypes.forEach(prefix => {
    Array.from({ length: TRUST_ENTITY_NAMES_NUMBER }).forEach((__, i) => {
      buildKeys(prefix, i + 1).forEach(key => {
        fields[key] = trustData[key];
      });
    });
  });

  return fields;
};

export const buildTitleHolderApplicationData = ({ closingInfo, trustInfo }) => {
  const vestingData = closingInfo || {};
  const trustData = trustInfo || {};

  return {
    [TitleHolderFieldId.VESTING_DESCRIPTION]: vestingData.vestingDescription,
    [TitleHolderFieldId.VESTING_TYPE_ID]: vestingData.vestingTypeId,
    [TitleHolderFieldId.MANNER_HELD]: vestingData.mannerHeld,
    [TitleHolderFieldId.BPL_COMPANY_NAME]: vestingData.bplCompanyName,
    ...(trustData.id
      ? {
          [TrustFieldId.TYPE]: trustData.type,
          [TrustFieldId.ESTABLISHED_DATE]: trustData.establishedDate,
          [TrustFieldId.NAME]: trustData.name,
          [TrustFieldId.ESTABLISHED_STATE]: trustData.establishedState,
          ...buildTrustEntityFields(trustData, TRUST_ENTITY_TYPES, (prefix, order) => {
            const keys = [`${prefix}${order}`];

            if (prefix !== 'beneficiary') {
              keys.push(`${prefix}${order}BorrowerId`);
            }

            if (prefix === 'trustee') {
              keys.push(`${prefix}${order}IsOwner`);
            }

            return keys;
          }),
        }
      : {}),
  };
};

export const buildTitleHolderPropertyPayload = formData => {
  const mannerHeld =
    formData[TitleHolderFieldId.MANNER_HELD]?.value ?? formData[TitleHolderFieldId.MANNER_HELD];
  const vestingTypeId =
    formData[TitleHolderFieldId.VESTING_TYPE_ID]?.value ??
    formData[TitleHolderFieldId.VESTING_TYPE_ID];

  return replaceEmptyWithNull({
    [TitleHolderFieldId.BPL_COMPANY_NAME]: formData[TitleHolderFieldId.BPL_COMPANY_NAME] || null,
    ...getConditionalObject(!!mannerHeld, { [TitleHolderFieldId.MANNER_HELD]: mannerHeld }),
    [TitleHolderFieldId.VESTING_DESCRIPTION]:
      formData[TitleHolderFieldId.VESTING_DESCRIPTION] || null,
    ...getConditionalObject(TitleHolderFieldId.VESTING_TYPE_ID in formData, {
      [TitleHolderFieldId.VESTING_TYPE_ID]: vestingTypeId ?? null,
    }),
    requestMetadata: { tabId: LoanApplicationTabId.PROPERTY },
  });
};

export const buildTitleHolderTrustPayload = formData => {
  const entityFields = {};

  TRUST_ENTITY_TYPES.forEach(prefix => {
    Array.from({ length: TRUST_ENTITY_NAMES_NUMBER }).forEach((__, i) => {
      const order = i + 1;
      entityFields[`${prefix}${order}`] = formData[`${prefix}${order}`] || null;

      if (prefix !== 'beneficiary') {
        entityFields[`${prefix}${order}BorrowerId`] =
          formData[`${prefix}${order}BorrowerId`] || null;
      }

      if (prefix === 'trustee') {
        entityFields[`trustee${order}IsOwner`] = formData[`trustee${order}IsOwner`] || false;
      }
    });
  });

  return replaceEmptyWithNull({
    ...entityFields,
    type: formData[TrustFieldId.TYPE]?.value ?? formData[TrustFieldId.TYPE] ?? null,
    name: formData[TrustFieldId.NAME] || null,
    establishedDate: getCommonDateBody(formData[TrustFieldId.ESTABLISHED_DATE]),
    establishedState:
      formData[TrustFieldId.ESTABLISHED_STATE]?.value ??
      formData[TrustFieldId.ESTABLISHED_STATE] ??
      null,
    requestMetadata: { tabId: LoanApplicationTabId.PROPERTY },
  });
};

export const buildFundingPayload = formData => {
  const feeProviderId = formData[WireToInformationFieldId.FEE_PROVIDER_ID]?.value;
  const { feeProviderLocationId } = formData;

  const closingAgent = {
    feeProviderId,
    feeProviderLocationId,
    companyName: formData[WireToInformationFieldId.COMPANY_NAME],
    companyAddress: {
      street: formData.companyStreet,
      city: formData.companyCity,
      state: formData.companyState ? { abbreviation: formData.companyState } : null,
      zipCode: formData.companyZipCode,
    },
    city: formData.city,
    usState: formData.state ? { abbreviation: formData.state?.value } : null,
    bank: formData[WireToInformationFieldId.BANK],
    abaNumber: formData[WireToInformationFieldId.ABA_NUMBER],
    accountNumber: formData[WireToInformationFieldId.ACCOUNT_NUMBER],
    referenceNumber: formData[WireToInformationFieldId.REFERENCE_NUMBER],
    contact: formData[WireToInformationFieldId.CONTACT],
  };

  const furtherCreditTo =
    formData[WireToInformationFieldId.FURTHER_CREDIT_ABA_NUMBER] ||
    formData[WireToInformationFieldId.FURTHER_CREDIT_ACCOUNT_NUMBER] ||
    formData[WireToInformationFieldId.FURTHER_CREDIT_CONTACT]
      ? {
          abaNumber: formData[WireToInformationFieldId.FURTHER_CREDIT_ABA_NUMBER],
          accountNumber: formData[WireToInformationFieldId.FURTHER_CREDIT_ACCOUNT_NUMBER],
          contact: formData[WireToInformationFieldId.FURTHER_CREDIT_CONTACT],
        }
      : null;

  return {
    body: replaceEmptyWithNull({
      warehouseBankId: formData[FundingInformationFieldId.WAREHOUSE_BANK_ID]?.value,
      commitmentNumber: formData[FundingInformationFieldId.COMMITMENT_NUMBER]?.trim(),
      fundingConfirmationNumber: formData[FundingInformationFieldId.FUNDING_CONFIRMATION_NUMBER],
      fundingOrdered:
        formData[FundingInformationFieldId.WAREHOUSE_FUNDING_ORDERED] &&
        dayjs(formData[FundingInformationFieldId.WAREHOUSE_FUNDING_ORDERED]).format(
          COMMON_REQUEST_DATE_FORMAT,
        ),
      wire: replaceEmptyWithNull({
        amount: formData[FundingInformationFieldId.WIRE_AMOUNT],
        postedDate:
          formData[FundingInformationFieldId.WIRE_POSTED_DATE] &&
          dayjs(formData[FundingInformationFieldId.WIRE_POSTED_DATE]).format(
            COMMON_REQUEST_DATE_FORMAT,
          ),
        comments: formData[FundingInformationFieldId.WIRE_COMMENTS],
        curtailment: formData[FundingInformationFieldId.CURTAILMENT],
      }),
      wireToInformation: replaceEmptyWithNull({
        closingAgent,
        furtherCreditTo,
        warehouseBankId: formData[WireToInformationFieldId.WAREHOUSE_BANK_ID]?.value,
        wireNumber: formData[WireToInformationFieldId.WIRE_NUMBER],
        fedReferenceNumber: formData[WireToInformationFieldId.FED_REFERENCE_NUMBER],
        wireAmount: formData[WireToInformationFieldId.WIRE_AMOUNT],
        advanceAmountPercentage: formData[WireToInformationFieldId.ADVANCE_AMOUNT_PERCENT],
        advanceAmount: formData[WireToInformationFieldId.ADVANCE_AMOUNT],
        addAdvanceAmount: formData[WireToInformationFieldId.ADD_ADVANCE_AMOUNT],
      }),
    }),
  };
};

export const buildFundingApplicationData = fundingInfo => {
  const address = fundingInfo.closingAgentFeeProviderAddress;
  const feeProviderFullAddress = address
    ? getFormattedFullAddress({
        address: { ...address, state: address?.state?.abbreviation, zip: address?.zipCode },
      })
    : null;

  return {
    ...fundingInfo,
    [WireToInformationFieldId.FEE_PROVIDER_ADDRESS]: feeProviderFullAddress,
    [WireToInformationFieldId.CITY]: fundingInfo.city,
    [WireToInformationFieldId.STATE]: statesWithNationalCategoryOptions.find(
      i => i.value === fundingInfo.state?.abbreviation,
    ),
    companyStreet: address?.street,
    companyCity: address?.city,
    companyState: address?.state?.abbreviation,
    companyZipCode: address?.zipCode,
  };
};

export const buildCompanyApplicationFields = (company, prefix) => {
  if (!company) {
    return {};
  }

  const fieldId = getCompanyFieldIds(prefix);

  return {
    [fieldId.attendantCompanyId]: company.attendantCompanyId || '',
    [fieldId.attendantCompanyLocationId]:
      company.closingCompanyLocationId || company.feeProviderLocationId || '',
    [fieldId.name]: company.name || '',
    [fieldId.licenseNumber]: company.licenseNumber || '',
    [fieldId.escrowNumber]: company.escrowNumber || '',
    [fieldId.streetAddress]: company.streetAddress || '',
    [fieldId.suite]: company.suite || '',
    [fieldId.city]: company.city || '',
    [fieldId.state]: company.state || '',
    [fieldId.zipCode]: company.zipCode || '',
    [fieldId.closerName]: company.closerName || '',
    [fieldId.closerContactEmail]: company.closerContactEmail || '',
    [fieldId.closerContactPhone]: getCleanPhoneNumber(company.closerContactPhone) || '',
    [fieldId.closerLicenseNumber]: company.closerLicenseNumber || '',
  };
};

export const buildAttorneyApplicationFields = (attorney, prefix) => {
  if (!attorney) {
    return {};
  }

  const fieldId = getAttorneyFieldIds(prefix);

  return {
    [fieldId.attendantCompanyId]: attorney.attendantCompanyId || '',
    [fieldId.attendantCompanyLocationId]:
      attorney.closingCompanyLocationId || attorney.feeProviderLocationId || '',
    [fieldId.name]: attorney.name || '',
    [fieldId.licenseNumber]: attorney.licenseNumber || '',
    [fieldId.streetAddress]: attorney.streetAddress || '',
    [fieldId.suite]: attorney.suite || '',
    [fieldId.city]: attorney.city || '',
    [fieldId.state]: attorney.state || '',
    [fieldId.zipCode]: attorney.zipCode || '',
    [fieldId.phone]: getCleanPhoneNumber(attorney.phone) || '',
    [fieldId.email]: attorney.email || '',
    [fieldId.contactPerson]: attorney.contactPerson || '',
    [fieldId.contactPhone]: getCleanPhoneNumber(attorney.contactPhone) || '',
    [fieldId.contactEmail]: attorney.contactEmail || '',
  };
};

export const getFieldWithFallback = (formData, key, fallback) =>
  key in formData ? formData[key] : fallback;

export const extractCompanyFormData = (formData, prefix, company) => {
  const fieldId = getCompanyFieldIds(prefix);

  return {
    attendantCompanyId: formData[fieldId.attendantCompanyId] || '',
    attendantCompanyLocationId: getFieldWithFallback(
      formData,
      fieldId.attendantCompanyLocationId,
      company?.closingCompanyLocationId || company?.feeProviderLocationId || '',
    ),
    streetAddress: getFieldWithFallback(
      formData,
      fieldId.streetAddress,
      company?.streetAddress || '',
    ),
    city: getFieldWithFallback(formData, fieldId.city, company?.city || ''),
    state: getFieldWithFallback(formData, fieldId.state, company?.state || ''),
    zipCode: getFieldWithFallback(formData, fieldId.zipCode, company?.zipCode || ''),
  };
};

const buildAttorneyPayloadEntry = (formData, attorney, prefix, closingCompanyType) => {
  const fieldId = getAttorneyFieldIds(prefix);
  const rawId = formData[fieldId.attendantCompanyId];
  const attendantCompanyId = (rawId?.value ?? rawId) || null;

  if (!attendantCompanyId) {
    return null;
  }

  const locationId = getFieldWithFallback(
    formData,
    fieldId.attendantCompanyLocationId,
    attorney?.closingCompanyLocationId || attorney?.feeProviderLocationId || null,
  );

  return {
    attendantCompanyId,
    closingCompanyType,
    location: {
      attendantCompanyLocationId: locationId,
      name: formData[fieldId.name] || attorney?.name || '',
      streetAddress: formData[fieldId.streetAddress] || attorney?.streetAddress || '',
      city: formData[fieldId.city] || attorney?.city || '',
      state: formData[fieldId.state] || attorney?.state || '',
      zipCode: formData[fieldId.zipCode] || attorney?.zipCode || '',
      suite: formData[fieldId.suite] || attorney?.suite || '',
      licenseNumber: formData[fieldId.licenseNumber] || attorney?.licenseNumber || '',
      phone: normalizeRequestPhoneNumber(formData[fieldId.phone]) || attorney?.phone || '',
      email: formData[fieldId.email] || attorney?.email || '',
      contactPerson: formData[fieldId.contactPerson] || attorney?.contactPerson || '',
      contactPhone:
        normalizeRequestPhoneNumber(formData[fieldId.contactPhone]) || attorney?.contactPhone || '',
      contactEmail: formData[fieldId.contactEmail] || attorney?.contactEmail || '',
    },
  };
};

const buildCompanyPayloadEntry = (formData, company, prefix) => {
  const fieldId = getCompanyFieldIds(prefix);
  const rawId = formData[fieldId.attendantCompanyId];
  const attendantCompanyId = (rawId?.value ?? rawId) || null;

  if (!attendantCompanyId) {
    return null;
  }

  const locationId = getFieldWithFallback(
    formData,
    fieldId.attendantCompanyLocationId,
    company?.closingCompanyLocationId || company?.feeProviderLocationId || null,
  );

  return {
    attendantCompanyId,
    escrowNumber: formData[fieldId.escrowNumber] || '',
    closerName: formData[fieldId.closerName] || '',
    closerContactEmail: formData[fieldId.closerContactEmail] || '',
    closerContactPhone: normalizeRequestPhoneNumber(formData[fieldId.closerContactPhone]) || '',
    closerLicenseNumber: formData[fieldId.closerLicenseNumber] || '',
    closingCompanyType:
      prefix === 'title' ? AttendantCompanyTypeEnum.TITLE : AttendantCompanyTypeEnum.ESCROW,
    location: {
      attendantCompanyLocationId: locationId,
      name: formData[fieldId.name] || company?.name || '',
      streetAddress: formData[fieldId.streetAddress] || company?.streetAddress || '',
      city: formData[fieldId.city] || company?.city || '',
      state: formData[fieldId.state] || company?.state || '',
      zipCode: formData[fieldId.zipCode] || company?.zipCode || '',
      suite: formData[fieldId.suite] || company?.suite || '',
      licenseNumber: formData[fieldId.licenseNumber] || company?.licenseNumber || '',
      accountName: company?.accountName || '',
      routingNumber: company?.routingNumber || '',
      accountNumber: company?.accountNumber || '',
    },
  };
};

export const prefixChanges = (changes, prefix) => {
  if (_.isNil(changes)) {
    return {};
  }

  return _.reduce(
    Object.entries(changes),
    (acc, [fieldName, change]) => {
      const prefixedName = `${prefix}${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
      acc[prefixedName] = change;

      return acc;
    },
    {},
  );
};

const getClearedCompanyIds = (formData, companies) =>
  companies.reduce((ids, { company, fieldIdKey }) => {
    if (company?.attendantCompanyId && fieldIdKey in formData && !formData[fieldIdKey]) {
      ids.push(company.attendantCompanyId);
    }

    return ids;
  }, []);

export const isCompanySectionChanged = ({
  formData,
  initialFormData,
  titleCompany,
  escrowCompany,
  sellerAttorney,
  buyerAttorney,
}) => {
  const hasClearedCompanies = !!getClearedCompanyIds(formData, [
    { company: titleCompany, fieldIdKey: getCompanyFieldIds('title').attendantCompanyId },
    { company: escrowCompany, fieldIdKey: getCompanyFieldIds('escrow').attendantCompanyId },
    {
      company: sellerAttorney,
      fieldIdKey: getAttorneyFieldIds('sellerAttorney').attendantCompanyId,
    },
    {
      company: buyerAttorney,
      fieldIdKey: getAttorneyFieldIds('buyerAttorney').attendantCompanyId,
    },
  ]).length;

  if (hasClearedCompanies) {
    return true;
  }

  const payloadPairs = [
    [
      buildCompanyPayloadEntry(formData, titleCompany, 'title'),
      buildCompanyPayloadEntry(initialFormData, titleCompany, 'title'),
    ],
    [
      buildCompanyPayloadEntry(formData, escrowCompany, 'escrow'),
      buildCompanyPayloadEntry(initialFormData, escrowCompany, 'escrow'),
    ],
    [
      buildAttorneyPayloadEntry(
        formData,
        sellerAttorney,
        'sellerAttorney',
        AttendantCompanyTypeEnum.SELLER_ATTORNEY,
      ),
      buildAttorneyPayloadEntry(
        initialFormData,
        sellerAttorney,
        'sellerAttorney',
        AttendantCompanyTypeEnum.SELLER_ATTORNEY,
      ),
    ],
    [
      buildAttorneyPayloadEntry(
        formData,
        buyerAttorney,
        'buyerAttorney',
        AttendantCompanyTypeEnum.BUYER_ATTORNEY,
      ),
      buildAttorneyPayloadEntry(
        initialFormData,
        buyerAttorney,
        'buyerAttorney',
        AttendantCompanyTypeEnum.BUYER_ATTORNEY,
      ),
    ],
  ];

  return payloadPairs.some(([current, initial]) => !_.isEqual(current, initial));
};

export const getClosingAttendantCompanies = ({
  formData,
  titleCompany,
  escrowCompany,
  sellerAttorney,
  buyerAttorney,
}) => {
  const titleEntry = buildCompanyPayloadEntry(formData, titleCompany, 'title');
  const escrowEntry = buildCompanyPayloadEntry(formData, escrowCompany, 'escrow');
  const sellerAttorneyEntry = buildAttorneyPayloadEntry(
    formData,
    sellerAttorney,
    'sellerAttorney',
    AttendantCompanyTypeEnum.SELLER_ATTORNEY,
  );
  const buyerAttorneyEntry = buildAttorneyPayloadEntry(
    formData,
    buyerAttorney,
    'buyerAttorney',
    AttendantCompanyTypeEnum.BUYER_ATTORNEY,
  );
  const closingAttendantCompanies = [
    titleEntry,
    escrowEntry,
    sellerAttorneyEntry,
    buyerAttorneyEntry,
  ].filter(Boolean);

  return closingAttendantCompanies;
};

export const applyCompanyNameHighlights = ({
  merged,
  locationPendingChanges,
  formPendingChanges,
  form,
}) => {
  locationPendingChanges.forEach(({ changes, formKey }) => {
    const namePc = changes?.name;

    if (!namePc) {
      return;
    }

    const formPc = formPendingChanges[formKey];

    if (!formPc) {
      merged[formKey] = namePc;

      return;
    }

    const formValue = form[formKey];
    const currentName = _.isObject(formValue) ? formValue.name : null;

    if (currentName === namePc.initialValue) {
      delete merged[formKey];
    }
  });
};
